// File: src/components/auth/Register.js

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { useLayout } from "../../contexts/LayoutContext";
import GoogleAddressAutocomplete from "../forms/GoogleAddressAutocomplete";
import api from "../../api/client";
import { resolveFeatureSections } from "../../utils/featureSections";
import logger from "../../utils/logger.js";
import {
  UserPlus,
  User,
  Mail,
  Phone,
  Lock,
  MapPin,
  AlertCircle,
  CheckCircle,
  Info,
} from "lucide-react";
import "./Register.css";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;

function buildAddressPayload(form) {
  return [
    form.streetAddress.trim(),
    form.neighborhood.trim(),
    form.city.trim(),
    form.countryName.trim(),
  ]
    .filter(Boolean)
    .join(", ");
}

function sanitizeNameInput(value) {
  return String(value || "")
    .replace(/[^\p{L}\s]/gu, "")
    .replace(/\s{2,}/g, " ");
}

function sanitizePhoneInput(value) {
  const raw = String(value || "").replace(/[^\d+]/g, "");
  const hasPlus = raw.startsWith("+");
  const digits = raw.replace(/\D/g, "");
  return `${hasPlus ? "+" : ""}${digits}`;
}

function sanitizeOtpInput(value) {
  return String(value || "").replace(/\D/g, "").slice(0, OTP_LENGTH);
}

export default function Register() {
  const navigate = useNavigate();

  const { lang } = useLanguage();
  const { settings, loadingSettings } = useLayout();
  const isAr = lang === "ar";

  const phoneVerificationEnabled =
    String(
      settings?.phone_verification_enabled ??
        settings?.phoneVerificationEnabled ??
        "1"
    ) === "1";

  const phoneVerificationSmsEnabled =
    String(
      settings?.phone_verification_sms_enabled ??
        settings?.phoneVerificationSmsEnabled ??
        "1"
    ) === "1";

  const phoneVerificationWhatsappEnabled =
    String(
      settings?.phone_verification_whatsapp_enabled ??
        settings?.phoneVerificationWhatsappEnabled ??
        "0"
    ) === "1";

  const phoneVerificationDefaultChannel =
    settings?.phone_verification_default_channel ??
    settings?.phoneVerificationDefaultChannel ??
    "sms";

  const [form, setForm] = useState({
    accountType: "customer",
    name: "",
    email: "",
    phone: "",
    phoneVerificationCode: "",
    password: "",
    passwordConfirm: "",
    addressSearch: "",
    streetAddress: "",
    neighborhood: "",
    city: "",
    countryName: "",
    countryCode: "",
    addressPlaceId: "",
    acceptTerms: false,
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [countryLoading, setCountryLoading] = useState(true);
  const [countryLoadError, setCountryLoadError] = useState("");
  const [smsCodeSent, setSmsCodeSent] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [sendingSmsCode, setSendingSmsCode] = useState(false);
  const [checkingSmsCode, setCheckingSmsCode] = useState(false);
  const [, setGoogleReady] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  const autoVerifyTimeoutRef = useRef(null);
  const lastAutoVerifiedCodeRef = useRef("");
  const lastVerifiedPhoneRef = useRef("");

  const labels = {
    en: {
      title: "Create an Account",
      subtitle: "Choose how you want to start, then create your Mousa Group account.",
      accountTypeTitle: "Account Type",
      accountTypeHint:
        "All options create one account first. Provider, course, consultation, service, job, and seller activities are completed later from the Provider Center.",
      accountTypes: {
        customer: {
          label: "Customer",
          description: "Shop products, save favorites, and manage your orders.",
        },
        trainer: {
          label: "Trainer",
          description: "Publish courses after admin approval from the Provider Center.",
        },
        consultant: {
          label: "Consultant",
          description: "Offer consultations after admin approval from the Provider Center.",
        },
        freelancer: {
          label: "Service Provider",
          description: "Sell freelance services after admin approval from the Provider Center.",
        },
        employer: {
          label: "Employer",
          description: "Post jobs after admin approval from the Provider Center.",
        },
        seller: {
          label: "Seller",
          description: "Sell physical or digital products after admin approval from the Provider Center.",
        },
      },
      immediateAccess: "Available right after email verification",
      approvalRequired: "Requires admin approval",
      providerNextStep:
        "After verifying your email, log in and go to Profile > Provider Center to submit your request. The admin must approve your account before you can publish content or products.",
      sellerDisabled:
        "Seller registration is currently hidden because the seller section is disabled from admin settings.",
      name: "Full Name",
      email: "Email",
      phone: "Phone",
      phoneVerificationCode: "Verification Code",
      sendCode: "Send Code",
      resendCode: "Resend Code",
      verifyCode: "Verify Code",
      verifying: "Verifying...",
      verified: "Phone verified",
      phoneMustBeVerified: "Please verify your phone number before creating the account.",
      sendCodeFirst: "Send the SMS code first.",
      phoneCodeRequired: "Please enter the verification code sent to your phone.",
      codeSent: "Code submitted for delivery. Please check your phone.",
      codeVerified: "Phone number verified successfully.",
      resendIn: "Resend in",
      password: "Password",
      passwordConfirm: "Confirm Password",
      addressSearch: "Search Address",
      streetAddress: "Street Address",
      neighborhood: "Neighborhood",
      city: "City",
      country: "Country",
      countryDetecting: "Detecting your country from IP...",
      countryDetectFailed: "We could not detect your country from IP. Please reload and try again.",
      addressRequired: "Please choose your address.",
      streetRequired: "Please provide your street address.",
      neighborhoodRequired: "Please provide your neighborhood.",
      cityRequired: "Please provide your city.",
      countryRequired: "Please choose your country.",
      countryCodeRequired: "Please provide a valid 2-letter country code.",
      nameValidation: "Name can contain letters and spaces only.",
      phoneValidation: "Phone must start with + and contain digits only.",
      phoneCodeValidation: "Verification code must contain digits only.",
      countryLockedHint: "Country is filled automatically and cannot be changed manually.",
      googleReadyHint: "Start typing and choose a real address from Google suggestions.",
      googleMissingKey: "Address search is not configured right now.",
      googleLoadFailed: "Address search could not be loaded. Please try again in a moment.",
      googleLoading: "Loading address suggestions...",
      googleNoResults: "No matching addresses found.",
      submit: "Sign Up",
      haveAccount: "Already have an account?",
      login: "Login",
      generalError: "Something went wrong, please try again.",
      success: "Account created successfully! Please verify your email.",
      termsLabel: "I agree to the terms and conditions",
      termsRequired: "You must accept the terms and conditions.",
      passwordMismatch: "Passwords do not match.",
      fillAll: "Please fill in all required fields.",
      otpDisabledSubmitHint: "Phone verification is currently disabled.",
      otpChannelUnavailable: "Phone verification is currently unavailable.",
    },
    ar: {
      title: "إنشاء حساب جديد",
      subtitle: "اختر طريقة استخدامك للموقع ثم أنشئ حسابك في موسى جروب.",
      accountTypeTitle: "نوع الحساب",
      accountTypeHint:
        "جميع الخيارات تنشئ حسابًا واحدًا أولًا. أما الكورسات والاستشارات والخدمات والوظائف ومنتجات البائعين فتُفعّل لاحقًا من خلال مركز مقدم الخدمة.",
      accountTypes: {
        customer: {
          label: "عميل",
          description: "تسوّق المنتجات، احفظ المفضلة، وتابع طلباتك.",
        },
        trainer: {
          label: "مدرب",
          description: "انشر الكورسات بعد موافقة الإدارة من مركز مقدم الخدمة.",
        },
        consultant: {
          label: "مستشار",
          description: "قدّم الاستشارات بعد موافقة الإدارة من مركز مقدم الخدمة.",
        },
        freelancer: {
          label: "مقدم خدمة",
          description: "اعرض خدماتك بعد موافقة الإدارة من مركز مقدم الخدمة.",
        },
        employer: {
          label: "صاحب وظائف",
          description: "أضف الوظائف بعد موافقة الإدارة من مركز مقدم الخدمة.",
        },
        seller: {
          label: "بائع",
          description: "بع منتجات مادية أو رقمية بعد موافقة الإدارة من مركز مقدم الخدمة.",
        },
      },
      immediateAccess: "متاح مباشرة بعد تفعيل البريد الإلكتروني",
      approvalRequired: "يتطلب موافقة الإدارة",
      providerNextStep:
        "بعد تفعيل بريدك الإلكتروني، سجّل الدخول ثم اذهب إلى الملف الشخصي > مركز مقدم الخدمة وقدّم طلبك. يجب أن توافق الإدارة على الحساب قبل نشر المحتوى أو المنتجات.",
      sellerDisabled:
        "خيار التسجيل كبائع مخفي حاليًا لأن قسم البائعين معطّل من إعدادات الإدارة.",
      name: "الاسم الكامل",
      email: "البريد الإلكتروني",
      phone: "رقم الهاتف",
      phoneVerificationCode: "كود التحقق",
      sendCode: "إرسال الكود",
      resendCode: "إعادة إرسال الكود",
      verifyCode: "تحقق من الكود",
      verifying: "جارٍ التحقق...",
      verified: "تم التحقق من الهاتف",
      phoneMustBeVerified: "يرجى التحقق من رقم الهاتف قبل إنشاء الحساب.",
      sendCodeFirst: "أرسل كود التحقق أولًا.",
      phoneCodeRequired: "يرجى إدخال كود التحقق المرسل إلى هاتفك.",
      codeSent: "تم إرسال الكود لمزوّد الرسائل. يرجى التحقق من هاتفك.",
      codeVerified: "تم التحقق من رقم الهاتف بنجاح.",
      resendIn: "إعادة الإرسال خلال",
      password: "كلمة المرور",
      passwordConfirm: "تأكيد كلمة المرور",
      addressSearch: "ابحث عن العنوان",
      streetAddress: "عنوان الشارع",
      neighborhood: "الحي",
      city: "المدينة",
      country: "الدولة",
      countryDetecting: "جاري تحديد دولتك من عنوان IP...",
      countryDetectFailed: "تعذر تحديد الدولة من عنوان IP. أعد تحميل الصفحة ثم حاول مرة أخرى.",
      addressRequired: "يرجى اختيار العنوان.",
      streetRequired: "يرجى إدخال عنوان الشارع.",
      neighborhoodRequired: "يرجى إدخال الحي.",
      cityRequired: "يرجى إدخال المدينة.",
      countryRequired: "يرجى اختيار الدولة.",
      countryCodeRequired: "يرجى إدخال رمز دولة صحيح من حرفين.",
      nameValidation: "يسمح في الاسم بالأحرف والمسافات فقط.",
      phoneValidation: "يجب أن يبدأ الهاتف بعلامة + ثم أرقام فقط.",
      phoneCodeValidation: "يجب أن يحتوي كود التحقق على أرقام فقط.",
      countryLockedHint: "يتم تعبئة الدولة تلقائيًا ولا يمكن تعديلها يدويًا.",
      googleReadyHint: "ابدأ بكتابة العنوان ثم اختر عنوانًا حقيقيًا من اقتراحات Google.",
      googleMissingKey: "بحث العنوان غير مهيأ حاليًا.",
      googleLoadFailed: "تعذر تحميل بحث العنوان. حاول مرة أخرى بعد قليل.",
      googleLoading: "جاري تحميل اقتراحات العنوان...",
      googleNoResults: "لم يتم العثور على عناوين مطابقة.",
      submit: "تسجيل",
      haveAccount: "لديك حساب بالفعل؟",
      login: "تسجيل الدخول",
      generalError: "حدث خطأ ما، حاول مرة أخرى.",
      success: "تم إنشاء الحساب بنجاح! يرجى تفعيل بريدك الإلكتروني.",
      termsLabel: "أوافق على الشروط والأحكام",
      termsRequired: "يجب الموافقة على الشروط والأحكام.",
      passwordMismatch: "كلمتا المرور غير متطابقتين.",
      fillAll: "الرجاء تعبئة جميع الحقول المطلوبة.",
      otpDisabledSubmitHint: "التحقق من الهاتف معطّل حاليًا.",
      otpChannelUnavailable: "خدمة التحقق من الهاتف غير متاحة حاليًا.",
    },
  }[lang];

  const sections = useMemo(
    () =>
      resolveFeatureSections(settings, {
        courses: true,
        consultations: true,
        services: true,
        jobs: true,
        sellerMarketplace: loadingSettings ? false : true,
      }),
    [loadingSettings, settings]
  );

  const accountOptions = useMemo(() => {
    return [
      { key: "customer", requiresApproval: false, enabled: true },
      { key: "trainer", requiresApproval: true, enabled: sections.courses },
      { key: "consultant", requiresApproval: true, enabled: sections.consultations },
      { key: "freelancer", requiresApproval: true, enabled: sections.services },
      { key: "employer", requiresApproval: true, enabled: sections.jobs },
      { key: "seller", requiresApproval: true, enabled: sections.sellerMarketplace },
    ].filter((option) => option.enabled);
  }, [sections]);

  const hasAnyOtpChannel =
    phoneVerificationSmsEnabled || phoneVerificationWhatsappEnabled;

  const canSendSmsCode =
    phoneVerificationEnabled &&
    hasAnyOtpChannel &&
    /^\+[0-9]{6,20}$/.test(form.phone.trim()) &&
    !sendingSmsCode &&
    resendCountdown === 0;

  const canVerifyCode =
    phoneVerificationEnabled &&
    smsCodeSent &&
    /^\+[0-9]{6,20}$/.test(form.phone.trim()) &&
    /^[0-9]{6}$/.test(form.phoneVerificationCode.trim()) &&
    !checkingSmsCode &&
    !phoneVerified;

  useEffect(() => {
    const isCurrentTypeAvailable =
      form.accountType === "customer" ||
      accountOptions.some((option) => option.key === form.accountType);

    if (!isCurrentTypeAvailable) {
      setForm((prev) => ({ ...prev, accountType: "customer" }));
    }
  }, [accountOptions, form.accountType]);

  useEffect(() => {
    let cancelled = false;

    async function loadCountryFromIp() {
      setCountryLoading(true);
      setCountryLoadError("");

      try {
        const data = await api.apiGet("register-country");

        if (cancelled) return;

        // Handle case where country code might be null (e.g., localhost)
        const countryCode = data?.country_code;
        const countryName = data?.country_name;

        if (countryCode && countryName) {
          setForm((prev) => ({
            ...prev,
            countryCode: countryCode,
            countryName: countryName,
          }));
        } else {
          // Set default for local/private IP
          setForm((prev) => ({
            ...prev,
            countryCode: "US",
            countryName: "United States",
          }));
        }
      } catch (err) {
        if (!cancelled) {
          // Don't treat all errors as fatal - allow user to continue
          setForm((prev) => ({
            ...prev,
            countryCode: "US",
            countryName: "United States",
          }));
        }
      } finally {
        if (!cancelled) {
          setCountryLoading(false);
        }
      }
    }

    loadCountryFromIp();

    return () => {
      cancelled = true;
    };
  }, [labels.countryDetectFailed]);

  useEffect(() => {
    if (resendCountdown <= 0) return undefined;

    const timer = window.setInterval(() => {
      setResendCountdown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [resendCountdown]);

  useEffect(() => {
    if (autoVerifyTimeoutRef.current) {
      window.clearTimeout(autoVerifyTimeoutRef.current);
      autoVerifyTimeoutRef.current = null;
    }

    if (!phoneVerificationEnabled || !smsCodeSent || phoneVerified || checkingSmsCode) {
      return undefined;
    }

    const cleanCode = form.phoneVerificationCode.trim();
    const cleanPhone = form.phone.trim();

    if (!/^[0-9]{6}$/.test(cleanCode)) {
      return undefined;
    }

    if (
      lastAutoVerifiedCodeRef.current === `${cleanPhone}:${cleanCode}` &&
      lastVerifiedPhoneRef.current === cleanPhone
    ) {
      return undefined;
    }

    autoVerifyTimeoutRef.current = window.setTimeout(() => {
      handleCheckSmsCode(true);
    }, 350);

    return () => {
      if (autoVerifyTimeoutRef.current) {
        window.clearTimeout(autoVerifyTimeoutRef.current);
        autoVerifyTimeoutRef.current = null;
      }
    };
  }, [
    form.phoneVerificationCode,
    form.phone,
    smsCodeSent,
    phoneVerified,
    checkingSmsCode,
    phoneVerificationEnabled,
  ]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (
      !form.name.trim() ||
      !form.email.trim() ||
      !form.password ||
      !form.passwordConfirm ||
      !form.addressSearch.trim()
    ) {
      setError(labels.fillAll);
      return;
    }

    if (!/^[\p{L}]+(?:\s+[\p{L}]+)*$/u.test(form.name.trim())) {
      setError(labels.nameValidation);
      return;
    }

    if (
      phoneVerificationEnabled &&
      !/^\+[0-9]{6,20}$/.test(form.phone.trim())
    ) {
      setError(labels.phoneValidation);
      return;
    }

    if (phoneVerificationEnabled && !/^[0-9]{4,10}$/.test(form.phoneVerificationCode.trim())) {
      setError(labels.phoneCodeValidation);
      return;
    }

    if (phoneVerificationEnabled && !phoneVerified) {
      setError(labels.phoneMustBeVerified);
      return;
    }

    if (!form.streetAddress.trim()) {
      setError(labels.streetRequired);
      return;
    }

    if (!form.neighborhood.trim()) {
      setError(labels.neighborhoodRequired);
      return;
    }

    if (!form.city.trim()) {
      setError(labels.cityRequired);
      return;
    }

    if (!form.countryName.trim()) {
      setError(countryLoadError || labels.countryRequired);
      return;
    }

    if (!/^[A-Za-z]{2}$/.test(form.countryCode.trim())) {
      setError(countryLoadError || labels.countryCodeRequired);
      return;
    }

    if (form.password !== form.passwordConfirm) {
      setError(labels.passwordMismatch);
      return;
    }

    if (!form.acceptTerms) {
      setError(labels.termsRequired);
      return;
    }

    if (countryLoading) {
      setError(labels.countryDetecting);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: form.name,
        email: form.email,
        account_type: form.accountType,
        ...(phoneVerificationEnabled
          ? { phone: form.phone.trim() }
          : {}),
        ...(phoneVerificationEnabled
          ? { phone_verification_code: form.phoneVerificationCode.trim() }
          : {}),
        password: form.password,
        address: buildAddressPayload(form),
        street_address: form.streetAddress.trim(),
        neighborhood: form.neighborhood.trim(),
        city: form.city.trim(),
        country_code: form.countryCode.trim(),
        country_name: form.countryName.trim(),
        address_place_id: form.addressPlaceId.trim() || undefined,
      };

      const data = await api.apiPostJson("register", payload);

      if (!data?.token && !data?.user) {
        throw new Error(data?.message || labels.generalError);
      }

      setSuccess(labels.success);

      localStorage.setItem("verify_email", form.email);
      localStorage.setItem("register_intent", form.accountType);

      setTimeout(() => navigate("/verify-email"), 1200);
    } catch (err) {
      logger.error("Register error:", err);
      setError(err?.message || labels.generalError);
    } finally {
      setLoading(false);
    }
  }

  const handleChange = (field) => (e) => {
    const value =
      field === "acceptTerms"
        ? e.target.checked
        : field === "name"
          ? sanitizeNameInput(e.target.value)
          : field === "phone"
            ? sanitizePhoneInput(e.target.value)
            : field === "phoneVerificationCode"
              ? sanitizeOtpInput(e.target.value)
              : e.target.value;

    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "phone" && prev.phone !== value
        ? { phoneVerificationCode: "" }
        : {}),
    }));

    if (field === "phone") {
      setSmsCodeSent(false);
      setPhoneVerified(false);
      setResendCountdown(0);
      lastAutoVerifiedCodeRef.current = "";
      lastVerifiedPhoneRef.current = "";
    }

    if (field === "phoneVerificationCode") {
      setPhoneVerified(false);
    }

    setError("");
    setSuccess("");
  };

  async function handleSendSmsCode() {
    setError("");
    setSuccess("");

    if (!phoneVerificationEnabled) {
      return;
    }

    if (!hasAnyOtpChannel) {
      setError(labels.otpChannelUnavailable);
      return;
    }

    if (!/^\+[0-9]{6,20}$/.test(form.phone.trim())) {
      setError(labels.phoneValidation);
      return;
    }

    if (resendCountdown > 0) {
      return;
    }

    setSendingSmsCode(true);

    try {
      const result = await api.apiPostJson("phone-verification/send", {
        phone: form.phone.trim(),
        locale: isAr ? "ar" : "en",
        channel: phoneVerificationDefaultChannel,
      });

      setSmsCodeSent(true);
      setPhoneVerified(false);
      setResendCountdown(RESEND_COOLDOWN_SECONDS);
      lastAutoVerifiedCodeRef.current = "";
      lastVerifiedPhoneRef.current = "";
      setSuccess(result?.message || labels.codeSent);
    } catch (err) {
      setError(err?.message || labels.generalError);
    } finally {
      setSendingSmsCode(false);
    }
  }

  async function handleCheckSmsCode(isAuto = false) {
    setError("");
    if (!isAuto) {
      setSuccess("");
    }

    if (!phoneVerificationEnabled) {
      return;
    }

    if (!smsCodeSent) {
      setError(labels.sendCodeFirst);
      return;
    }

    if (!/^\+[0-9]{6,20}$/.test(form.phone.trim())) {
      setError(labels.phoneValidation);
      return;
    }

    if (!/^[0-9]{6}$/.test(form.phoneVerificationCode.trim())) {
      setError(labels.phoneCodeValidation);
      return;
    }

    const verificationKey = `${form.phone.trim()}:${form.phoneVerificationCode.trim()}`;

    if (checkingSmsCode || (phoneVerified && lastVerifiedPhoneRef.current === form.phone.trim())) {
      return;
    }

    setCheckingSmsCode(true);

    try {
      const result = await api.apiPostJson("phone-verification/check", {
        phone: form.phone.trim(),
        code: form.phoneVerificationCode.trim(),
      });

      setPhoneVerified(true);
      lastAutoVerifiedCodeRef.current = verificationKey;
      lastVerifiedPhoneRef.current = form.phone.trim();
      setSuccess(result?.message || labels.codeVerified);
    } catch (err) {
      setPhoneVerified(false);
      if (!isAuto) {
        setError(err?.message || labels.generalError);
      }
    } finally {
      setCheckingSmsCode(false);
    }
  }

  const handleAddressChange = (value) => {
    setForm((prev) => ({
      ...prev,
      addressSearch: value,
      streetAddress: "",
      neighborhood: "",
      city: "",
      countryName: prev.countryName,
      countryCode: prev.countryCode,
      addressPlaceId: "",
    }));
    setError("");
    setSuccess("");
  };

  const handleAddressSelect = (selection) => {
    setForm((prev) => ({
      ...prev,
      addressSearch: selection.address || prev.addressSearch,
      streetAddress: selection.streetAddress || "",
      neighborhood: selection.neighborhood || "",
      city: selection.city || "",
      countryName: prev.countryName || selection.countryName || "",
      countryCode: prev.countryCode || selection.countryCode || "",
      addressPlaceId: selection.placeId || "",
    }));
    setError("");
    setSuccess("");
  };

  return (
    <div className="register-page" dir={isAr ? "rtl" : "ltr"}>
      <div className="register-container">
        <div className="register-card">
          <div className="register-header">
            <div className="register-header-icon">
              <UserPlus size={24} strokeWidth={2} />
            </div>
            <h1 className="register-header-title">{labels.title}</h1>
            <p className="register-header-subtitle">{labels.subtitle}</p>
          </div>

          <div className="register-account-section">
            <div className="register-account-title">{labels.accountTypeTitle}</div>
            <div className="register-account-hint">{labels.accountTypeHint}</div>

            <div className="register-account-grid">
              {accountOptions.map((option) => {
                const optionLabels = labels.accountTypes[option.key];
                const isSelected = form.accountType === option.key;

                return (
                  <button
                    type="button"
                    key={option.key}
                    className={`register-account-button ${isSelected ? "active" : ""}`}
                    onClick={() =>
                      handleChange("accountType")({ target: { value: option.key } })
                    }
                  >
                    <div className="register-account-label">
                      <span>{optionLabels.label}</span>
                      <span
                        className={`register-account-badge ${
                          option.requiresApproval ? "approval" : "immediate"
                        }`}
                      >
                        {option.requiresApproval
                          ? labels.approvalRequired
                          : labels.immediateAccess}
                      </span>
                    </div>
                    <p className="register-account-description">
                      {optionLabels.description}
                    </p>
                  </button>
                );
              })}
            </div>

            {form.accountType !== "customer" && (
              <div className="register-account-alert">
                <Info size={16} />
                <span>{labels.providerNextStep}</span>
              </div>
            )}

            {!loadingSettings && !sections.sellerMarketplace && (
              <div className="register-account-disabled">{labels.sellerDisabled}</div>
            )}
          </div>

          {error && (
            <div className="register-message error" role="alert" aria-live="polite">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="register-message success">
              <CheckCircle size={16} />
              <span>{success}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="register-form">
            <div className="register-form-group">
              <label htmlFor="register-name" className="register-form-label">{labels.name}</label>
              <div className="register-input-wrapper">
                <div className="register-input-icon">
                  <User size={18} strokeWidth={2} />
                </div>
                <input
                  id="register-name"
                  type="text"
                  className="register-input"
                  placeholder={labels.name}
                  value={form.name}
                  onChange={handleChange("name")}
                  required
                />
              </div>
            </div>

            <div className="register-form-group">
              <label htmlFor="register-email" className="register-form-label">{labels.email}</label>
              <div className="register-input-wrapper">
                <div className="register-input-icon">
                  <Mail size={18} strokeWidth={2} />
                </div>
                <input
                  id="register-email"
                  type="email"
                  className="register-input"
                  placeholder={labels.email}
                  value={form.email}
                  onChange={handleChange("email")}
                  required
                />
              </div>
            </div>

            {phoneVerificationEnabled && (
              <div className="register-form-group">
                <label className="register-form-label">{labels.phone}</label>
                <div className="register-input-wrapper">
                  <div className="register-input-icon">
                    <Phone size={18} strokeWidth={2} />
                  </div>
                  <input
                    type="tel"
                    className="register-input"
                    placeholder={labels.phone}
                    value={form.phone}
                    onChange={handleChange("phone")}
                    inputMode="tel"
                    autoComplete="tel"
                  />
                </div>
              </div>
            )}

            {phoneVerificationEnabled && (
              <div className="register-phone-section">
                <div className="register-phone-actions">
                  <button
                    type="button"
                    className="register-button-action"
                    onClick={handleSendSmsCode}
                    disabled={!canSendSmsCode}
                  >
                    <Phone size={14} />
                    <span>
                      {sendingSmsCode
                        ? "..."
                        : smsCodeSent
                          ? labels.resendCode
                          : labels.sendCode}
                    </span>
                  </button>

                  {resendCountdown > 0 && !phoneVerified && (
                    <p className="register-phone-status">
                      {labels.resendIn} {resendCountdown}s
                    </p>
                  )}

                  {phoneVerified && (
                    <div className="register-phone-verified">
                      <CheckCircle size={14} />
                      <span>{labels.verified}</span>
                    </div>
                  )}
                </div>

                <div className="register-verification-row">
                  <div className="register-form-group" style={{ flex: 1 }}>
                    <label className="register-form-label">
                      {labels.phoneVerificationCode}
                    </label>
                    <div className="register-input-wrapper">
                      <div className="register-input-icon">
                        <Lock size={18} strokeWidth={2} />
                      </div>
                      <input
                        type="text"
                        className="register-input"
                        placeholder={labels.phoneVerificationCode}
                        value={form.phoneVerificationCode}
                        onChange={handleChange("phoneVerificationCode")}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={OTP_LENGTH}
                        required
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    className="register-button-action"
                    onClick={() => handleCheckSmsCode(false)}
                    disabled={!canVerifyCode}
                    style={{ marginBottom: 0 }}
                  >
                    <span>
                      {checkingSmsCode ? labels.verifying : labels.verifyCode}
                    </span>
                  </button>
                </div>
              </div>
            )}

            {!phoneVerificationEnabled && (
              <p className="register-phone-status" style={{ margin: 0 }}>
                {labels.otpDisabledSubmitHint}
              </p>
            )}

            <div className="register-form-group">
              <label className="register-form-label">{labels.addressSearch}</label>
              <GoogleAddressAutocomplete
                className="register-input"
                placeholder={labels.addressSearch}
                value={form.addressSearch}
                onChange={handleAddressChange}
                onSelect={handleAddressSelect}
                onReadyChange={setGoogleReady}
                required
                languageCode={isAr ? "ar" : "en"}
                regionCode={form.countryCode}
                statusTexts={{
                  readyHint: labels.googleReadyHint,
                  missingKey: labels.googleMissingKey,
                  loadFailed: labels.googleLoadFailed,
                  loading: labels.googleLoading,
                  noResults: labels.googleNoResults,
                }}
              />
            </div>

            <div className="register-form-group">
              <label className="register-form-label">{labels.streetAddress}</label>
              <div className="register-input-wrapper">
                <div className="register-input-icon">
                  <MapPin size={18} strokeWidth={2} />
                </div>
                <input
                  type="text"
                  className="register-input"
                  placeholder={labels.streetAddress}
                  value={form.streetAddress}
                  onChange={handleChange("streetAddress")}
                  required
                />
              </div>
            </div>

            <div className="register-form-group">
              <label className="register-form-label">{labels.neighborhood}</label>
              <div className="register-input-wrapper">
                <div className="register-input-icon">
                  <MapPin size={18} strokeWidth={2} />
                </div>
                <input
                  type="text"
                  className="register-input"
                  placeholder={labels.neighborhood}
                  value={form.neighborhood}
                  onChange={handleChange("neighborhood")}
                  required
                />
              </div>
            </div>

            <div className="register-form-group">
              <label className="register-form-label">{labels.city}</label>
              <div className="register-input-wrapper">
                <div className="register-input-icon">
                  <MapPin size={18} strokeWidth={2} />
                </div>
                <input
                  type="text"
                  className="register-input"
                  placeholder={labels.city}
                  value={form.city}
                  onChange={handleChange("city")}
                  required
                />
              </div>
            </div>

            <div className="register-form-group">
              <label className="register-form-label">{labels.country}</label>
              <div className="register-input-wrapper">
                <div className="register-input-icon">
                  <MapPin size={18} strokeWidth={2} />
                </div>
                <input
                  type="text"
                  className="register-input"
                  placeholder={labels.country}
                  value={form.countryName}
                  readOnly
                  disabled
                />
              </div>
              <p className="register-phone-status">
                {countryLoading
                  ? labels.countryDetecting
                  : countryLoadError || labels.countryLockedHint}
              </p>
            </div>

            <div className="register-form-group">
              <label htmlFor="register-password" className="register-form-label">{labels.password}</label>
              <div className="register-input-wrapper">
                <div className="register-input-icon">
                  <Lock size={18} strokeWidth={2} />
                </div>
                <input
                  id="register-password"
                  type="password"
                  className="register-input"
                  placeholder={labels.password}
                  value={form.password}
                  onChange={handleChange("password")}
                  autoComplete="new-password"
                  required
                />
              </div>
            </div>

            <div className="register-form-group">
              <label htmlFor="register-passwordConfirm" className="register-form-label">{labels.passwordConfirm}</label>
              <div className="register-input-wrapper">
                <div className="register-input-icon">
                  <Lock size={18} strokeWidth={2} />
                </div>
                <input
                  id="register-passwordConfirm"
                  type="password"
                  className="register-input"
                  placeholder={labels.passwordConfirm}
                  value={form.passwordConfirm}
                  onChange={handleChange("passwordConfirm")}
                  autoComplete="new-password"
                  required
                />
              </div>
            </div>

            <div className="register-checkbox-group">
              <input
                className="register-checkbox-input"
                type="checkbox"
                id="acceptTerms"
                checked={form.acceptTerms}
                onChange={handleChange("acceptTerms")}
                required
              />
              <label className="register-checkbox-label" htmlFor="acceptTerms">
                {labels.termsLabel}
              </label>
            </div>

            <button
              type="submit"
              className="register-button-submit"
              disabled={loading || (phoneVerificationEnabled && !phoneVerified)}
            >
              <UserPlus size={16} />
              <span>{loading ? "..." : labels.submit}</span>
            </button>
          </form>

          <div className="register-login-footer">
            <span>{labels.haveAccount} </span>
            <Link to="/login" className="register-login-link">
              {labels.login}
            </Link>
          </div>
        </div>

        <p className="register-copyright">
          © {new Date().getFullYear()} Mousa Group LLC
        </p>
      </div>
    </div>
  );
}